import React, { useEffect, useState } from 'react';
import { Box, Card, Grid, Typography } from '@mui/material';
import api from '../../../Website/utils/api';

// Icons / colours per card slot; the status key and title come from CARD_SETS below
const cardStyles = [
    {
        iconBg: 'linear-gradient(135deg, #64748B 0%, #334155 100%)',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
            </svg>
        ),
    },
    {
        iconBg: 'linear-gradient(135deg, #FF7E36 0%, #F86624 100%)',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
            </svg>
        ),
    },
    {
        iconBg: 'linear-gradient(135deg, #44C0FF 0%, #2BB2FE 100%)',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13" rx="2"/>
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                <circle cx="5.5" cy="18.5" r="2.5"/>
                <circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
        ),
    },
    {
        iconBg: 'linear-gradient(135deg, #00D2B4 0%, #1A9882 100%)',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
        ),
    },
];

// Which statuses each page shows; "total" is the combined count
const CARD_SETS = {
    orders: [
        { key: 'NEW', title: 'New' },
        { key: 'PROCESSING', title: 'Processing' },
        { key: 'SHIPPED', title: 'Shipped' },
        { key: 'DELIVERED', title: 'Delivered' },
    ],
    schedule: [
        { key: 'NEW', title: 'New' },
        { key: 'APPROVED', title: 'Approved' },
        { key: 'DISPATCHED', title: 'Dispatched' },
        { key: 'COMPLETED', title: 'Completed' },
    ],
    enquiries: [
        { key: 'total', title: 'Total enquiries' },
        { key: 'NEW', title: 'Pending' },
        { key: 'QUOTED', title: 'Quoted' },
        { key: 'COMPLETED', title: 'Completed' },
    ],
};

const DashCards = ({ type = 'orders' }) => {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        let alive = true;
        setStats(null);
        api.getCustomerStatistics(type)
            .then((res) => { if (alive) setStats(res?.data?.data || {}); })
            .catch(() => { if (alive) setStats({}); });
        return () => { alive = false; };
    }, [type]);

    const orderStatuses = (CARD_SETS[type] || CARD_SETS.orders).map((card, i) => {
        const row = stats?.[card.key];
        return {
            title: card.title,
            count: stats ? (row?.count || 0) : '–',
            today: `+${row?.todayCount || 0} today`,
            ...cardStyles[i],
        };
    });

    return (
        <Box sx={{ paddingBottom: '16px' }}>
            <Grid container spacing={2}>
                {orderStatuses.map((status, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            border: '1px solid #E0E2E7',
                            borderRadius: '12px',
                            boxShadow: 'none',
                            padding: '16px 20px',
                            boxSizing: 'border-box',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                                borderColor: '#CBD5E1',
                            }
                        }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Typography variant="subtitle1" color="#777980" fontSize="14px" fontWeight="600">
                                    {status.title}
                                </Typography>
                                <Typography variant="h5" color="#1D1F2C" fontSize="28px" fontWeight="700" sx={{ lineHeight: 1.2 }}>
                                    {status.count}
                                </Typography>
                                <Typography variant="body2" color="#858D9D" fontSize="13px" fontWeight="500">
                                    {status.today}
                                </Typography>
                            </Box>
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                background: status.iconBg,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                            }}>
                                {status.icon}
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default DashCards;
